<template>
    <Card
        v-if="!localData.recinto && localData.start_event_date"
        icon="fas fa-share-alt"
        bg-color="tw:bg-[var(--sitecolor)]"
        title-color="white"
        title-classes="tw:border-b tw:border-gray-200"
        :title="'Entradas compartidas'"
        padding="0"
    >
        <div class="tw:p-6 ">
            <div v-if="!localData.recinto && localData.start_event_date">
                <ToggleSwitch
                    :value="localData.entradas_compartidas"
                    @input="val => set(localData, 'entradas_compartidas', val)"
                    variant="primary"
                    id="entradas_otros_eventos"
                >
                    {{ 'Activar entradas compartidas' }}
                    <Tooltip
                        text="Cuando el evento no tiene recinto, puedes compartir entradas con otros eventos."
                        variant="success"
                    />
                </ToggleSwitch>

                <div v-if="localData.entradas_compartidas" class="tw:mt-4" ref="entradasCompartidasSection">
                    <template v-if="eventos?.length">
                        <label class="tw:block tw:text-sm tw:font-medium tw:text-gray-700 tw:mb-1">
                            {{ 'Selecciona los eventos de los que quieres cargar las entradas' }}
                        </label>
                        <BaseMultiselect
                            :label="'Eventos'"
                            name="eventos_compartidos"
                            :value="localData.eventos_compartidos"
                            :options="eventos"
                            label-key="name"
                            value-key="id"
                            :multiple="true"
                            @input="val => onEventosCompartidosChange(val)"
                        />
                    </template>
                    <Skeleton
                        v-else-if="eventosLoading"
                        padding="tw:p-0"
                        variant="tw:bg-transparent tw:shadow-none"
                    >
                        <template #skeleton>
                            <SkeletonItem height="tw:h-[38px]" customClass="tw:w-full" />
                        </template>
                    </Skeleton>
                    <div v-else class="tw:text-sm tw:text-gray-500">
                        {{ 'No hay eventos disponibles para compartir entradas.' }}
                    </div>
                </div>
            </div>

            <div v-else class="tw:py-4 tw:text-gray-400 tw:text-sm">
                <i class="fas fa-info-circle tw:mr-1"></i>
                {{ 'Las entradas compartidas solo están disponibles para eventos sin recinto y con fecha de inicio definida.' }}
            </div>
        </div>
    </Card>
</template>

<script setup>
    import { inject, set, ref } from 'vue';
    import { useScrollOnToggle } from '@/composables/common/useScrollOnToggle';
    import Card from '@/modules/admin/ui/Card.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import Tooltip from '@/modules/admin/ui/Tooltip.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    const localData = inject('eventoFormData');

    const props = defineProps({
        eventos: { type: Array, default: () => [] },
        eventosLoading: { type: Boolean, default: false }
    });

    const entradasCompartidasSection = ref(null);
    useScrollOnToggle(() => localData.entradas_compartidas, entradasCompartidasSection);

    // BaseMultiselect (post-fix) emits a plain array of IDs — see
    // BaseMultiselect.vue `handleSelect`. But the BACK_EVENTS_CREAR submit
    // contract — and `compartirEventos` in Crear.vue / Editar.vue, which does
    // `evento.id` on the result — expects the legacy shape: an array of
    // hydrated event objects (`{id, name, tiene_sesiones, place_id, …}`),
    // matching what master sent with vue-multiselect's `track-by="id"`.
    // Resolve each emitted ID back to its source option, falling back to the
    // raw ID if it's no longer in `eventos` (e.g. the user kept a selection
    // across a refetch and the option dropped) so we never lose it silently.
    function onEventosCompartidosChange(val) {
        const ids = Array.isArray(val) ? val : [];
        localData.eventos_compartidos = ids.map(id =>
            props.eventos.find(e => e.id === id) || id
        );
    }

    // Exposed for unit tests so they can drive the multiselect contract
    // without depending on BaseMultiselect's internal emit timing.
    defineExpose({ onEventosCompartidosChange });
</script>
